import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { apiService } from '../services/api';
import { S3Object, Bucket } from '../types';
import BulkActionBar from './BulkActionBar';
import ShareDialog from './ShareDialog';

interface ObjectListProps {
  bucketName: string;
  bucket?: Bucket;
  refreshTrigger: number;
  allBuckets?: Bucket[];
}

const ObjectList: React.FC<ObjectListProps> = ({ bucketName, bucket, refreshTrigger, allBuckets = [] }) => {
  const [objects, setObjects] = useState<S3Object[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  
  // Selection state for bulk operations
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [indeterminateFolders, setIndeterminateFolders] = useState<Set<string>>(new Set());
  const [bulkOperationProgress, setBulkOperationProgress] = useState<{
    operation: string;
    current: number;
    total: number;
  } | null>(null);

  // Copy/Move dialog state
  const [copyMoveDialog, setCopyMoveDialog] = useState<{ operation: 'copy' | 'move'; keys: string[] } | null>(null);
  const [copyMoveDestBucket, setCopyMoveDestBucket] = useState('');

  // Share dialog state
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareDialogObject, setShareDialogObject] = useState<{ bucketName: string; objectKey: string } | null>(null);
  const [activeShares, setActiveShares] = useState<Array<{
    shareToken: string;
    bucketName: string;
    objectKey: string;
    shareUrl: string;
    createdAt: string;
    expiresAt: string;
  }>>([]);
  const [sharesLoading, setSharesLoading] = useState(false);

  const getDisplayName = (): string => {
    if (bucket?.displayName) return bucket.displayName;
    // Format: crate-{uid8}-{region}-{name}
    const match = bucketName.match(/^crate-[0-9a-f]{8}-[^-]+-(.+)$/);
    return match ? match[1] : bucketName;
  };

  const displayName = getDisplayName();

  // Computed values for selected objects
  const selectedObjects = useMemo(() => 
    objects.filter(obj => selectedKeys.has(obj.key)),
    [objects, selectedKeys]
  );

  const activeSelected = useMemo(() => 
    selectedObjects.filter(obj => !obj.isDeleted),
    [selectedObjects]
  );

  const deletedSelected = useMemo(() => 
    selectedObjects.filter(obj => obj.isDeleted),
    [selectedObjects]
  );

  useEffect(() => {
    fetchObjects();
    // Only fetch shares for OVH buckets
    if (bucket?.provider === 'OVH') {
      fetchShares();
    }
  }, [bucketName, refreshTrigger]);

  // Helper function: Get all child object keys within a folder
  const getAllChildKeys = useCallback((folderPath: string): string[] => {
    const childKeys: string[] = [];
    
    objects.forEach(obj => {
      if (obj.key.startsWith(folderPath + '/')) {
        childKeys.push(obj.key);
      }
    });
    
    return childKeys;
  }, [objects]);

  // Helper function: Extract all folder paths from objects
  const extractFolderPaths = useCallback((): string[] => {
    const folderSet = new Set<string>();
    
    objects.forEach(obj => {
      const parts = obj.key.split('/');
      let currentPath = '';
      
      // Build folder paths (exclude the file name)
      for (let i = 0; i < parts.length - 1; i++) {
        currentPath = currentPath ? `${currentPath}/${parts[i]}` : parts[i];
        folderSet.add(currentPath);
      }
    });
    
    return Array.from(folderSet);
  }, [objects]);

  // Helper function: Update indeterminate states for folders
  const updateIndeterminateStates = useCallback((selected: Set<string>) => {
    const indeterminate = new Set<string>();
    const folderPaths = extractFolderPaths();
    
    folderPaths.forEach(folderPath => {
      const childKeys = getAllChildKeys(folderPath);
      const selectedCount = childKeys.filter(key => selected.has(key)).length;
      
      if (selectedCount > 0 && selectedCount < childKeys.length) {
        indeterminate.add(folderPath);
      }
    });
    
    setIndeterminateFolders(indeterminate);
  }, [getAllChildKeys, extractFolderPaths]);

  // Handler: Folder selection (select/deselect all children)
  const handleFolderSelection = useCallback((folderPath: string, checked: boolean) => {
    const childKeys = getAllChildKeys(folderPath);
    
    // Warn if selecting a large folder
    if (checked && childKeys.length > 100) {
      if (!window.confirm(
        `This folder contains ${childKeys.length} objects.\n\n` +
        `Selecting large folders may take time. Continue?`
      )) {
        return;
      }
    }
    
    const newSelected = new Set(selectedKeys);
    
    if (checked) {
      childKeys.forEach(key => newSelected.add(key));
    } else {
      childKeys.forEach(key => newSelected.delete(key));
    }
    
    setSelectedKeys(newSelected);
    updateIndeterminateStates(newSelected);
  }, [selectedKeys, getAllChildKeys, updateIndeterminateStates]);

  // Handler: Individual file checkbox change
  const handleCheckboxChange = useCallback((key: string, checked: boolean) => {
    const newSelected = new Set(selectedKeys);
    
    if (checked) {
      newSelected.add(key);
    } else {
      newSelected.delete(key);
    }
    
    setSelectedKeys(newSelected);
    updateIndeterminateStates(newSelected);
  }, [selectedKeys, updateIndeterminateStates]);

  // Handler: Clear selection
  const clearSelection = useCallback(() => {
    setSelectedKeys(new Set());
    setIndeterminateFolders(new Set());
  }, []);

  const toggleFolder = (folderPath: string) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderPath)) {
      newExpanded.delete(folderPath);
    } else {
      newExpanded.add(folderPath);
    }
    setExpandedFolders(newExpanded);
  };

  const buildFolderTree = () => {
    const tree: any = {};
    
    objects.forEach(obj => {
      const parts = obj.key.split('/');
      let current = tree;
      
      parts.forEach((part, index) => {
        if (index === parts.length - 1) {
          // It's a file
          if (!current._files) current._files = [];
          current._files.push(obj);
        } else {
          // It's a folder
          if (!current[part]) {
            current[part] = {};
          }
          current = current[part];
        }
      });
    });
    
    return tree;
  };

  const renderObjectTree = () => {
    const tree = buildFolderTree();
    return renderTree(tree, '');
  };

  const renderTree = (node: any, path: string, level: number = 0): JSX.Element[] => {
    const elements: JSX.Element[] = [];
    
    // Render folders
    Object.keys(node).forEach(key => {
      if (key === '_files') return;
      
      const folderPath = path ? `${path}/${key}` : key;
      const isExpanded = expandedFolders.has(folderPath);
      
      // Calculate folder selection state
      const childKeys = getAllChildKeys(folderPath);
      const selectedCount = childKeys.filter(k => selectedKeys.has(k)).length;
      const isFolderChecked = childKeys.length > 0 && selectedCount === childKeys.length;
      const isFolderIndeterminate = selectedCount > 0 && selectedCount < childKeys.length;
      const isDisabled = bulkOperationProgress !== null;
      
      elements.push(
        <div key={folderPath} style={{ marginLeft: `${level * 20}px` }}>
          <div 
            style={{ 
              padding: '0.75rem',
              backgroundColor: isFolderChecked ? '#e3f2fd' : '#f9fafb',
              borderRadius: '4px',
              marginBottom: '0.5rem',
              display: 'flex',
              alignItems: 'center',
              fontWeight: 500,
              cursor: 'pointer',
              opacity: isDisabled ? 0.6 : 1
            }}
            onMouseEnter={(e) => {
              if (!isDisabled) {
                e.currentTarget.style.backgroundColor = isFolderChecked ? '#bbdefb' : '#f3f4f6';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = isFolderChecked ? '#e3f2fd' : '#f9fafb';
            }}
          >
            <input
              type="checkbox"
              checked={isFolderChecked}
              ref={(input) => {
                if (input) {
                  input.indeterminate = isFolderIndeterminate;
                }
              }}
              onChange={(e) => {
                e.stopPropagation();
                handleFolderSelection(folderPath, e.target.checked);
              }}
              disabled={isDisabled}
              style={{ 
                marginRight: '8px',
                cursor: isDisabled ? 'not-allowed' : 'pointer'
              }}
              onClick={(e) => e.stopPropagation()}
            />
            <span 
              onClick={() => toggleFolder(folderPath)}
              style={{ display: 'flex', alignItems: 'center', flex: 1 }}
            >
              <span style={{ marginRight: '0.5rem' }}>{isExpanded ? '📂' : '📁'}</span>
              {key}
            </span>
          </div>
          {isExpanded && renderTree(node[key], folderPath, level + 1)}
        </div>
      );
    });
    
    // Render files
    if (node._files) {
      node._files.forEach((obj: S3Object) => {
        const fileName = obj.key.split('/').pop() || obj.key;
        const isChecked = selectedKeys.has(obj.key);
        const isDisabled = bulkOperationProgress !== null;
        const isLyveBucket = bucket?.provider === 'OVH';
        
        elements.push(
          <div 
            key={obj.key} 
            className={`object-item ${obj.isDeleted ? 'deleted' : ''}`} 
            style={{ 
              marginLeft: `${level * 20}px`,
              backgroundColor: isChecked ? '#e3f2fd' : 'transparent',
              opacity: isDisabled ? 0.6 : 1,
              padding: '0.75rem',
              borderRadius: '4px',
              marginBottom: '0.5rem'
            }}
            onMouseEnter={(e) => {
              if (!isDisabled) {
                e.currentTarget.style.backgroundColor = isChecked ? '#bbdefb' : '#f9fafb';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = isChecked ? '#e3f2fd' : 'transparent';
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={(e) => handleCheckboxChange(obj.key, e.target.checked)}
                  disabled={isDisabled}
                  style={{ 
                    marginRight: '8px',
                    cursor: isDisabled ? 'not-allowed' : 'pointer'
                  }}
                />
                <div style={{ flex: 1 }}>
                  <div className={`object-name ${obj.isDeleted ? 'deleted' : ''}`} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    📄 {fileName}
                    {obj.isDeleted && <span className="object-badge badge-deleted">Deleted</span>}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#666', marginTop: '0.25rem' }}>
                    Size: {(obj.size / 1024).toFixed(2)} KB | 
                    Modified: {new Date(obj.lastModified).toLocaleString()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      });
    }
    
    return elements;
  };

  const fetchObjects = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await apiService.listObjectVersions(bucketName);
      
      console.log('Raw API response:', response);
      console.log('Versions array:', response.versions);
      
      // Group versions by key - only show the latest version for each unique key
      const latestVersions = new Map<string, any>();
      
      if (response.versions && response.versions.length > 0) {
        response.versions.forEach((version) => {
          const normalizedKey = version.key.trim(); // Normalize key
          
          console.log(`Processing version: key="${normalizedKey}", isLatest=${version.isLatest}, isDeleteMarker=${version.isDeleteMarker}`);
          
          const existing = latestVersions.get(normalizedKey);
          
          // Priority: Delete markers take precedence over regular versions when both are latest
          if (!existing) {
            latestVersions.set(normalizedKey, version);
            console.log(`  -> Added as new entry`);
          } else if (version.isLatest && version.isDeleteMarker) {
            // Delete marker with isLatest=true always wins
            latestVersions.set(normalizedKey, version);
            console.log(`  -> Replaced with delete marker (latest)`);
          } else if (version.isLatest && !existing.isDeleteMarker) {
            // Only replace if existing is not a delete marker
            latestVersions.set(normalizedKey, version);
            console.log(`  -> Replaced with latest version`);
          } else {
            console.log(`  -> Skipped (not latest or delete marker exists)`);
          }
        });
      }
      
      console.log('Final latestVersions map:', latestVersions);
      
      // Convert to S3Object array
      const objectsList = Array.from(latestVersions.values()).map(version => ({
        key: version.key,
        size: version.size,
        lastModified: version.lastModified,
        isDeleted: version.isDeleteMarker,
        versionId: version.versionId,
        deleteMarker: version.isDeleteMarker,
      }));

      console.log('Final objects list:', objectsList);
      setObjects(objectsList);
    } catch (err: any) {
      console.error('Failed to fetch objects:', err);
      // Only show error if it's not a "no objects" scenario
      if (err.message && !err.message.includes('does not exist')) {
        setError('Failed to load objects');
      } else {
        // Bucket is empty, just set empty array
        setObjects([]);
      }
    } finally {
      setLoading(false);
    }
  };

  // Bulk operation: Download
  const handleBulkDownload = async () => {
    const toDownload = activeSelected;
    
    if (toDownload.length === 0) return;
    
    // Validate limits
    const totalSize = toDownload.reduce((sum, obj) => sum + obj.size, 0);
    const maxObjects = 1000;
    const maxSize = 5 * 1024 * 1024 * 1024; // 5GB
    
    if (toDownload.length > maxObjects) {
      if (!window.confirm(
        `You are about to download ${toDownload.length} objects (limit: ${maxObjects}).\n\n` +
        `This may take a long time. Continue?`
      )) {
        return;
      }
    }
    
    if (totalSize > maxSize) {
      alert(
        `Total size exceeds 5GB limit.\n\n` +
        `Selected: ${(totalSize / (1024 * 1024 * 1024)).toFixed(2)} GB\n` +
        `Please select fewer items.`
      );
      return;
    }
    
    // Single file - direct download
    if (toDownload.length === 1) {
      try {
        const response = await apiService.downloadObject(bucketName, toDownload[0].key);
        window.open(response.downloadUrl, '_blank');
        clearSelection();
      } catch (err: any) {
        console.error('Download error:', err);
        alert(err.message || 'Failed to download file');
      }
      return;
    }
    
    // Multiple files - create zip
    setBulkOperationProgress({
      operation: 'download',
      current: 0,
      total: toDownload.length
    });
    
    try {
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();
      
      for (let i = 0; i < toDownload.length; i++) {
        const obj = toDownload[i];
        
        try {
          const response = await apiService.downloadObject(bucketName, obj.key);
          const fileResponse = await fetch(response.downloadUrl);
          const blob = await fileResponse.blob();
          
          zip.file(obj.key, blob);
          
          setBulkOperationProgress({
            operation: 'download',
            current: i + 1,
            total: toDownload.length
          });
        } catch (err) {
          console.error(`Failed to download ${obj.key}:`, err);
          // Continue with other files
        }
      }
      
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${bucketName}-bulk-download.zip`;
      a.click();
      URL.revokeObjectURL(url);
      
      alert(`Successfully downloaded ${toDownload.length} item(s)`);
    } catch (err: any) {
      console.error('Bulk download error:', err);
      alert(err.message || 'Failed to download files');
    } finally {
      setBulkOperationProgress(null);
      clearSelection();
    }
  };

  // Bulk operation: Delete
  const handleBulkDelete = async () => {
    const toDelete = activeSelected;
    
    if (toDelete.length === 0) return;
    
    if (!window.confirm(`Delete ${toDelete.length} item(s)?`)) {
      return;
    }
    
    setBulkOperationProgress({
      operation: 'delete',
      current: 0,
      total: toDelete.length
    });
    
    const errors: Array<{ key: string; error: string }> = [];
    
    for (let i = 0; i < toDelete.length; i++) {
      const obj = toDelete[i];
      
      try {
        await apiService.deleteObject(bucketName, obj.key);
        
        setBulkOperationProgress({
          operation: 'delete',
          current: i + 1,
          total: toDelete.length
        });
      } catch (err: any) {
        errors.push({
          key: obj.key,
          error: err.message || 'Unknown error'
        });
      }
    }
    
    setBulkOperationProgress(null);
    clearSelection();
    await fetchObjects();
    
    // Refresh shares if this is a shareable bucket
    if (bucket?.provider === 'OVH') {
      await fetchShares();
    }

    if (errors.length > 0) {
      alert(`Failed to delete ${errors.length} item(s):\n${errors.map(e => e.key).join('\n')}`);
    } else {
      alert(`Successfully deleted ${toDelete.length} item(s)`);
    }
  };

  // Bulk operation: Restore
  const handleBulkRestore = async () => {
    const toRestore = deletedSelected;
    
    if (toRestore.length === 0) return;
    
    setBulkOperationProgress({
      operation: 'restore',
      current: 0,
      total: toRestore.length
    });
    
    const errors: Array<{ key: string; error: string }> = [];
    
    for (let i = 0; i < toRestore.length; i++) {
      const obj = toRestore[i];
      
      try {
        await apiService.restoreObject(bucketName, obj.key);
        
        setBulkOperationProgress({
          operation: 'restore',
          current: i + 1,
          total: toRestore.length
        });
      } catch (err: any) {
        errors.push({
          key: obj.key,
          error: err.message || 'Unknown error'
        });
      }
    }
    
    setBulkOperationProgress(null);
    clearSelection();
    await fetchObjects();
    
    if (errors.length > 0) {
      alert(`Failed to restore ${errors.length} item(s):\n${errors.map(e => e.key).join('\n')}`);
    } else {
      alert(`Successfully restored ${toRestore.length} item(s)`);
    }
  };

  // Bulk operation: Permanent Delete
  const handleBulkPermanentDelete = async () => {
    const toDelete = deletedSelected;
    
    if (toDelete.length === 0) return;
    
    if (!window.confirm(
      `PERMANENTLY DELETE ${toDelete.length} item(s)?\n\n` +
      `This action CANNOT be undone and will delete all versions.`
    )) {
      return;
    }
    
    setBulkOperationProgress({
      operation: 'permanentDelete',
      current: 0,
      total: toDelete.length
    });
    
    const errors: Array<{ key: string; error: string }> = [];
    
    for (let i = 0; i < toDelete.length; i++) {
      const obj = toDelete[i];
      
      try {
        await apiService.permanentlyDeleteObject(bucketName, obj.key);
        
        setBulkOperationProgress({
          operation: 'permanentDelete',
          current: i + 1,
          total: toDelete.length
        });
      } catch (err: any) {
        errors.push({
          key: obj.key,
          error: err.message || 'Unknown error'
        });
      }
    }
    
    setBulkOperationProgress(null);
    clearSelection();
    await fetchObjects();
    
    // Refresh shares if this is a shareable bucket
    if (bucket?.provider === 'OVH') {
      await fetchShares();
    }

    if (errors.length > 0) {
      alert(`Failed to permanently delete ${errors.length} item(s):\n${errors.map(e => e.key).join('\n')}`);
    } else {
      alert(`Successfully permanently deleted ${toDelete.length} item(s)`);
    }
  };

  // Bulk operation: Copy / Move
  const handleBulkCopyMove = (operation: 'copy' | 'move') => {
    if (activeSelected.length === 0) return;
    const otherBuckets = allBuckets.filter(b => b.name !== bucketName);
    if (otherBuckets.length === 0) {
      alert('No other crates available. Create another crate first.');
      return;
    }
    setCopyMoveDestBucket(otherBuckets[0].name);
    setCopyMoveDialog({ operation, keys: activeSelected.map(o => o.key) });
  };

  const handleConfirmCopyMove = async () => {
    if (!copyMoveDialog || !copyMoveDestBucket) return;
    const { operation, keys } = copyMoveDialog;
    setCopyMoveDialog(null);
    setBulkOperationProgress({ operation, current: 0, total: keys.length });
    try {
      const result = await apiService.copyObjects(
        bucketName, copyMoveDestBucket, keys, operation,
        (done, total) => setBulkOperationProgress({ operation, current: done, total })
      );
      if (result.failedCount > 0) {
        alert(`${result.copiedCount} succeeded, ${result.failedCount} failed.`);
      } else {
        alert(`${operation === 'move' ? 'Moved' : 'Copied'} ${result.copiedCount} file(s) successfully.`);
      }
      clearSelection();
      if (operation === 'move') await fetchObjects();
    } catch (err: any) {
      alert(err.message || `${operation} failed`);
    } finally {
      setBulkOperationProgress(null);
    }
  };

  // Share-related functions
  const fetchShares = async () => {
    setSharesLoading(true);
    try {
      console.log('Fetching shares for bucket:', bucketName);
      console.log('About to call apiService.listShares()...');
      
      let response;
      try {
        response = await apiService.listShares();
        console.log('✅ Shares API response received:', response);
      } catch (apiError: any) {
        console.error('❌ API call failed with error:', {
          message: apiError.message,
          name: apiError.name,
          stack: apiError.stack,
          fullError: apiError
        });
        // Show error to user
        alert(`Failed to load shares: ${apiError.message}`);
        throw apiError;
      }
      
      console.log('Shares API response:', response);
      if (response.success) {
        // Filter shares for current bucket
        const bucketShares = response.shares.filter(share => share.bucketName === bucketName);
        console.log('Filtered shares for bucket:', bucketShares);
        setActiveShares(bucketShares);
      } else {
        console.warn('Shares API returned success=false:', response);
      }
    } catch (err: any) {
      console.error('❌ Failed to fetch shares - Error details:', {
        message: err.message,
        stack: err.stack,
        error: err
      });
    } finally {
      setSharesLoading(false);
      console.log('fetchShares completed');
    }
  };

  // Helper: Check if an object is shared
  const getShareForObject = (objectKey: string) => {
    return activeShares.find(share => share.objectKey === objectKey);
  };

  // Handler: Show share details
  const handleShowShareDetails = (objectKey: string) => {
    const share = getShareForObject(objectKey);
    if (!share) return;

    const expiresAt = new Date(share.expiresAt);
    const now = new Date();
    const hoursRemaining = Math.floor((expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60));
    const daysRemaining = Math.floor(hoursRemaining / 24);

    const message = `📄 ${objectKey.split('/').pop()}\n\n` +
      `🔗 Share Link:\n${share.shareUrl}\n\n` +
      `⏰ Expires in: ${daysRemaining > 0 ? `${daysRemaining} day(s)` : `${hoursRemaining} hour(s)`}\n` +
      `📅 Expiration: ${expiresAt.toLocaleString()}\n\n` +
      `Would you like to copy the link or revoke the share?`;

    if (window.confirm(message + '\n\nClick OK to copy link, Cancel to revoke share')) {
      handleCopyShareUrl(share.shareUrl);
    } else {
      handleRevokeShare(share.shareToken);
    }
  };

  const handleShareClick = (objectKey: string) => {
    if (bucket?.provider !== 'OVH') {
      alert('Sharing is only available for OVHcloud buckets');
      return;
    }

    setShareDialogObject({ bucketName, objectKey });
    setShareDialogOpen(true);
  };

  // Bulk operation: Share selected objects
  const handleBulkShare = () => {
    const toShare = activeSelected;
    
    if (toShare.length === 0) return;
    
    // Only allow sharing one object at a time for now
    if (toShare.length > 1) {
      alert('Please select only one object to share');
      return;
    }

    handleShareClick(toShare[0].key);
  };

  // Bulk operation: Unshare selected objects
  const handleBulkUnshare = async () => {
    const toUnshare = activeSelected.filter(obj => 
      activeShares.some(share => share.objectKey === obj.key)
    );
    
    if (toUnshare.length === 0) {
      alert('No shared objects selected');
      return;
    }

    const confirmMessage = toUnshare.length === 1
      ? `Unshare "${toUnshare[0].key.split('/').pop()}"?\n\nThe share link will no longer work.`
      : `Unshare ${toUnshare.length} objects?\n\nAll share links will no longer work.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setBulkOperationProgress({
      operation: 'unshare',
      current: 0,
      total: toUnshare.length
    });

    const errors: Array<{ key: string; error: string }> = [];

    for (let i = 0; i < toUnshare.length; i++) {
      const obj = toUnshare[i];
      const share = activeShares.find(s => s.objectKey === obj.key);
      
      if (!share) continue;

      try {
        const response = await apiService.revokeShare(share.shareToken);
        if (!response.success) {
          errors.push({
            key: obj.key,
            error: response.message || 'Failed to revoke share'
          });
        }

        setBulkOperationProgress({
          operation: 'unshare',
          current: i + 1,
          total: toUnshare.length
        });
      } catch (err: any) {
        errors.push({
          key: obj.key,
          error: err.message || 'Unknown error'
        });
      }
    }

    setBulkOperationProgress(null);
    clearSelection();
    await fetchShares();

    if (errors.length > 0) {
      alert(`Failed to unshare ${errors.length} item(s):\n${errors.map(e => e.key).join('\n')}`);
    } else {
      alert(`Successfully unshared ${toUnshare.length} item(s)`);
    }
  };

  const handleShareCreated = () => {
    fetchShares();
  };

  const handleRevokeShare = async (shareToken: string) => {
    if (!window.confirm('Revoke this share? The link will no longer work.')) {
      return;
    }

    try {
      const response = await apiService.revokeShare(shareToken);
      if (response.success) {
        alert('Share revoked successfully');
        fetchShares();
      } else {
        alert(response.message || 'Failed to revoke share');
      }
    } catch (err: any) {
      console.error('Failed to revoke share:', err);
      alert(err.message || 'Failed to revoke share');
    }
  };

  const handleCopyShareUrl = async (shareUrl: string) => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      alert('Share link copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      alert('Failed to copy to clipboard');
    }
  };

  return (
    <div className="object-list-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 600, margin: 0 }}>
          Data in {displayName}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <button
            onClick={fetchObjects}
            disabled={loading || bulkOperationProgress !== null}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: (loading || bulkOperationProgress !== null) ? '#e0e0e0' : '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: (loading || bulkOperationProgress !== null) ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.875rem',
              fontWeight: 500,
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              if (!loading && bulkOperationProgress === null) {
                e.currentTarget.style.backgroundColor = '#5568d3';
              }
            }}
            onMouseLeave={(e) => {
              if (!loading && bulkOperationProgress === null) {
                e.currentTarget.style.backgroundColor = '#667eea';
              }
            }}
            title="Refresh object list"
          >
            <span style={{ display: 'inline-block', animation: loading ? 'spin 1s linear infinite' : 'none' }}>
              🔄
            </span>
            Refresh
          </button>
        </div>
      </div>
      {/* Active Shares Section - Only for OVH buckets */}
      {(bucket?.provider === 'OVH') && activeShares.length > 0 && (
        <div style={{
          backgroundColor: '#f0f9ff',
          border: '1px solid #bae6fd',
          borderRadius: '8px',
          padding: '1rem',
          marginBottom: '1.5rem'
        }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.75rem' }}>
            🔗 Active Shares ({activeShares.length})
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {activeShares.map(share => {
              const fileName = share.objectKey.split('/').pop() || share.objectKey;
              const expiresAt = new Date(share.expiresAt);
              const now = new Date();
              const hoursRemaining = Math.floor((expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60));
              
              return (
                <div
                  key={share.shareToken}
                  style={{
                    backgroundColor: 'white',
                    padding: '0.75rem',
                    borderRadius: '6px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500, marginBottom: '0.25rem' }}>
                      📄 {fileName}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#666' }}>
                      Expires in {hoursRemaining}h ({expiresAt.toLocaleString()})
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      onClick={() => handleCopyShareUrl(share.shareUrl)}
                      style={{
                        padding: '0.5rem 0.75rem',
                        backgroundColor: '#667eea',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.75rem',
                        fontWeight: 500
                      }}
                      title="Copy share link"
                    >
                      📋 Copy
                    </button>
                    <button
                      onClick={() => handleRevokeShare(share.shareToken)}
                      style={{
                        padding: '0.5rem 0.75rem',
                        backgroundColor: '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.75rem',
                        fontWeight: 500
                      }}
                      title="Revoke share"
                    >
                      ✕ Revoke
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Bulk Action Bar */}
      <BulkActionBar
        selectedObjects={selectedObjects}
        onDownload={handleBulkDownload}
        onDelete={handleBulkDelete}
        onRestore={handleBulkRestore}
        onPermanentDelete={handleBulkPermanentDelete}
        onShare={handleBulkShare}
        onUnshare={handleBulkUnshare}
        onCopy={allBuckets.length > 1 ? () => handleBulkCopyMove('copy') : undefined}
        onMove={allBuckets.length > 1 ? () => handleBulkCopyMove('move') : undefined}
        onClearSelection={clearSelection}
        isProcessing={bulkOperationProgress !== null}
        isLyveBucket={bucket?.provider === 'OVH'}
        sharedObjectKeys={activeShares.map(share => share.objectKey)}
      />

      {/* Progress Indicator */}
      {bulkOperationProgress && (
        <div style={{
          padding: '1rem',
          backgroundColor: '#e3f2fd',
          borderRadius: '8px',
          marginBottom: '1rem',
          textAlign: 'center'
        }}>
          <div style={{ fontWeight: 500, marginBottom: '0.5rem' }}>
            {bulkOperationProgress.operation === 'download' && 'Downloading files...'}
            {bulkOperationProgress.operation === 'delete' && 'Deleting files...'}
            {bulkOperationProgress.operation === 'restore' && 'Restoring files...'}
            {bulkOperationProgress.operation === 'permanentDelete' && 'Permanently deleting files...'}
            {bulkOperationProgress.operation === 'unshare' && 'Revoking shares...'}
            {bulkOperationProgress.operation === 'copy' && `Copying files… (${bulkOperationProgress.current}/${bulkOperationProgress.total})`}
            {bulkOperationProgress.operation === 'move' && `Moving files… (${bulkOperationProgress.current}/${bulkOperationProgress.total})`}
          </div>
          <div style={{ fontSize: '0.875rem', color: '#666' }}>
            {bulkOperationProgress.current} / {bulkOperationProgress.total}
          </div>
        </div>
      )}

      {loading && <p>Loading objects...</p>}
      {error && <div className="form-error">{error}</div>}

      {!loading && !error && objects.length === 0 && (
        <p style={{ color: '#999', textAlign: 'center', padding: '2rem' }}>
          This data crate is empty. Upload a file to get started.
        </p>
      )}

      {renderObjectTree()}

      {/* Share Dialog */}
      {shareDialogObject && (
        <ShareDialog
          isOpen={shareDialogOpen}
          onClose={() => {
            setShareDialogOpen(false);
            setShareDialogObject(null);
          }}
          bucketName={shareDialogObject.bucketName}
          objectKey={shareDialogObject.objectKey}
          onShareCreated={handleShareCreated}
        />
      )}

      {/* Copy / Move dialog */}
      {copyMoveDialog && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }} onClick={() => setCopyMoveDialog(null)}>
          <div style={{
            background: '#fff', borderRadius: 12, width: 440, padding: '24px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.25)'
          }} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: 12 }}>
              {copyMoveDialog.operation === 'move' ? '✂️ Move' : '📋 Copy'} {copyMoveDialog.keys.length} file{copyMoveDialog.keys.length !== 1 ? 's' : ''}
            </h3>
            <p style={{ fontSize: '0.875rem', color: '#374151', marginBottom: 16 }}>
              From: <strong>{bucket?.displayName || bucketName}</strong>
            </p>
            <label style={{ display: 'block', fontWeight: 600, fontSize: '0.875rem', marginBottom: 8, color: '#374151' }}>
              Destination crate
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 240, overflowY: 'auto', marginBottom: 16 }}>
              {allBuckets.filter(b => b.name !== bucketName).map(b => (
                <label key={b.name} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                  border: `2px solid ${copyMoveDestBucket === b.name ? '#667eea' : '#e5e7eb'}`,
                  background: copyMoveDestBucket === b.name ? '#f0f0ff' : '#fff'
                }}>
                  <input
                    type="radio"
                    name="copyMoveDest"
                    value={b.name}
                    checked={copyMoveDestBucket === b.name}
                    onChange={() => setCopyMoveDestBucket(b.name)}
                    style={{ accentColor: '#667eea', flexShrink: 0 }}
                  />
                  <span style={{ fontSize: 18 }}>🗄️</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {b.displayName || b.name}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                      {b.provider === 'OVH' ? 'DC4S' : (b.provider || 'S3')}
                    </div>
                  </div>
                </label>
              ))}
            </div>
            {copyMoveDialog.operation === 'move' && (
              <div style={{
                background: '#fff7ed', border: '1px solid #fdba74', borderRadius: 8,
                padding: '8px 12px', fontSize: '0.8rem', color: '#9a3412', marginBottom: 16
              }}>
                Files will be copied to the destination and removed from this crate.
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setCopyMoveDialog(null)}
                style={{ padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: '0.875rem' }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmCopyMove}
                disabled={!copyMoveDestBucket}
                style={{
                  padding: '8px 20px', border: 'none', borderRadius: 8, cursor: 'pointer',
                  fontSize: '0.875rem', fontWeight: 600, color: '#fff',
                  background: copyMoveDialog.operation === 'move' ? '#7c3aed' : '#0891b2',
                  opacity: copyMoveDestBucket ? 1 : 0.5
                }}
              >
                {copyMoveDialog.operation === 'move' ? 'Move Here' : 'Copy Here'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ObjectList;
